import config from 'config/appConfig';
import { getPreviewUrl } from 'utils/voiceChat';
import { DivConst } from 'constants/divConst';
import { useState, useEffect } from 'react';
import { useAppState } from 'hooks/useAppState';
import type { VoiceChatResponseType } from 'types/api/voiceChatResponseType';

import type { JSX } from 'react';

const DEFAULT_BUTTON_LABEL = '音声アンケートに答える';

/**
 * ボイスチャット作成編集 ボタン埋め込みタグコンポーネント（モック 1174-1245 行相当）。
 * ボタン文言はフロント表示のみ・DB 非保持（再読込保持対象外）。
 */
export default function WsVoiceChatEditButtonEmbed(props: {
  voiceChatState: VoiceChatResponseType | null;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const [copied, setCopied] = useState(false);
  const [embedButtonLabel, setEmbedButtonLabel] = useState(DEFAULT_BUTTON_LABEL);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { noticeMessageSuccess } = useAppState();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsModalOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen]);

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  const _getPreviewUrl = (): string => {
    if (!props.voiceChatState?.previewCd) {
      return '';
    }
    return getPreviewUrl(props.voiceChatState.previewCd);
  };

  const _isVisiblePublishInfo = (): boolean => {
    if (!props.voiceChatState) {
      return false;
    }
    if (props.voiceChatState.voiceChatStatusDiv === DivConst.items.voiceChatStatus.reserved.value) {
      return true;
    }
    return props.voiceChatState.publishedAt != null;
  };

  const _generateEmbedCode = (): string => {
    if (!props.voiceChatState || !_isVisiblePublishInfo()) {
      return '';
    }

    const staticEndpoint = config.s3.bucket.staticContents.endpoint.replace(/\/$/, '');
    const userWebUrl = config.url.userWeb.replace(/\/$/, '');
    const embedJsUrl = `${staticEndpoint}/common/embed/branq-voice-chat-button.js`;
    const cfgJson = JSON.stringify({ staticEndpoint, userWebUrl });

    return [
      '<!-- BranQ Voice Chat Button Embed Tag -->',
      '<script>',
      '(function branqVoiceChatButtonBootstrap(w,d,s,u,c,e,t){',
      'c=w.BranqVoiceChatButton=w.BranqVoiceChatButton||function(){(c.q=c.q||[]).push(arguments)};',
      'c.q=c.q||[];',
      `c.cfg=${cfgJson};`,
      'e=d.createElement(s);e.async=1;e.src=u;',
      't=d.getElementsByTagName(s)[0];t.parentNode.insertBefore(e,t);',
      `})(window,document,'script',${JSON.stringify(embedJsUrl)});`,
      `BranqVoiceChatButton(${JSON.stringify(props.voiceChatState.cd)},${JSON.stringify(embedButtonLabel)});`,
      '</script>',
    ].join('\n');
  };

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  const onCopyCode = async () => {
    const embedCode = _generateEmbedCode();
    if (!embedCode) return;
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      noticeMessageSuccess('コードをコピーしました。');
    } catch (err) {
      console.error('コピーに失敗しました:', err);
    }
  };

  const embedCode = _generateEmbedCode();
  const hasEmbedCode = embedCode !== '';

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-form-default">
        <div className="container">
          <div className="box-common shadow-none">
            <h3 className="title-section title-md">ボタン埋め込みタグ</h3>
            <div className="form-default-container">
              {/* ボタンのプレビュー */}
              <div className="form-group">
                <label className="form-label">ボタンのプレビュー</label>
                <div className="vc-embed-preview">
                  <button
                    type="button"
                    className="vc-embed-btn"
                    onClick={() => setIsModalOpen(true)}
                  >
                    {embedButtonLabel || 'ボタン'}
                  </button>
                </div>
              </div>

              {/* ボタンの文言 */}
              <div className="form-group">
                <label className="form-label">ボタンの文言</label>
                <input
                  className="form-control"
                  value={embedButtonLabel}
                  onChange={e => setEmbedButtonLabel(e.target.value)}
                  placeholder="ボタンの文言"
                  style={{ maxWidth: 420 }}
                />
              </div>

              {/* 埋め込みタグ表示 */}
              <div className="form-group">
                <p className="mb-3">
                  下のコードをコピーして、ボタンを表示したい場所に追加してください。押すとボイスチャットがポップアップで開きます。
                </p>
                <div className="position-relative">
                  <textarea
                    className="form-control font-monospace"
                    rows={8}
                    readOnly
                    value={hasEmbedCode ? embedCode : ''}
                    placeholder="公開設定を行うと、ボタン埋め込みタグが表示されます。"
                    style={{ paddingRight: '50px' }}
                  />
                  <button
                    type="button"
                    onClick={onCopyCode}
                    disabled={!hasEmbedCode}
                    className="btn-empty position-absolute"
                    style={{ top: '8px', right: '8px', zIndex: 10 }}
                    title={copied ? 'コピーしました' : 'コピー'}
                  >
                    {copied ? (
                      <img src="/img/icon/copied.svg" alt="コピー済み" />
                    ) : (
                      <img src="/img/icon/copy.svg" alt="コピー" />
                    )}
                  </button>
                </div>
                <p className="mt-2 text-muted small">
                  ※コンテンツ公開時のみ表示されます。
                  <br />
                  ※文言を変更した場合もタグの再発行・埋め込みは不要です。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {isModalOpen && (
        <div
          className="branq-vc-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483647,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.5)',
          }}
          onClick={e => {
            if (e.target === e.currentTarget) {
              setIsModalOpen(false);
            }
          }}
        >
          <div
            role="dialog"
            className="branq-vc-modal"
            style={{
              position: 'relative',
              width: 480,
              height: 700,
              background: '#fff',
              borderRadius: 12,
              overflow: 'hidden',
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              className="branq-vc-close"
              style={{ position: 'absolute', top: 8, right: 12, zIndex: 1 }}
              onClick={() => setIsModalOpen(false)}
            >
              ×
            </button>
            <iframe
              className="branq-vc-frame"
              title="プレビュー"
              src={_getPreviewUrl()}
              allow="microphone"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        </div>
      )}
    </>
  );
}
